// Advanced Performance Monitoring for Makrana Marble React App
// Real User Monitoring (RUM) and Core Web Vitals Integration

class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.vitals = {};
    this.userSessions = [];
    this.currentSession = this.createSession();
    this.observers = new Map();
    this.isOnline = navigator.onLine;
    
    this.init();
  }
  
  init() {
    try {
      // Initialize Core Web Vitals monitoring
      this.initCoreWebVitals();
      
      // Initialize user interaction tracking
      this.initUserInteractionTracking();
      
      // Initialize resource performance monitoring
      this.initResourceMonitoring();
      
      // Initialize navigation timing
      this.initNavigationTiming();
      
      // Initialize memory monitoring
      this.initMemoryMonitoring();
      
      // Initialize error tracking
      this.initErrorTracking();
      
      // Initialize network monitoring
      this.initNetworkMonitoring();
      
      // Setup periodic reporting
      this.setupPeriodicReporting();
      
      console.log('Advanced Performance Monitor initialized');
    } catch (error) {
      console.error('Performance Monitor initialization failed:', error);
    }
  }
  
  createSession() {
    return {
      id: `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      startTime: performance.now(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      referrer: document.referrer,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      connection: this.getConnectionInfo(),
      interactions: [],
      pageViews: [],
      errors: [],
      performance: {}
    };
  }
  
  initCoreWebVitals() {
    // Largest Contentful Paint (LCP)
    this.observePerformanceEntry('largest-contentful-paint', (entries) => {
      const lastEntry = entries[entries.length - 1];
      this.vitals.lcp = {
        value: lastEntry.startTime,
        element: lastEntry.element?.tagName || 'unknown',
        url: lastEntry.url || '',
        timestamp: Date.now()
      };
      this.reportVital('LCP', this.vitals.lcp.value);
    });
    
    // First Input Delay (FID)
    this.observePerformanceEntry('first-input', (entries) => {
      const firstInput = entries[0];
      this.vitals.fid = {
        value: firstInput.processingStart - firstInput.startTime,
        eventType: firstInput.name,
        timestamp: Date.now()
      };
      this.reportVital('FID', this.vitals.fid.value);
    });
    
    // Cumulative Layout Shift (CLS)
    let clsValue = 0;
    this.observePerformanceEntry('layout-shift', (entries) => {
      for (const entry of entries) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      }
      
      this.vitals.cls = {
        value: clsValue,
        timestamp: Date.now()
      };
      this.reportVital('CLS', this.vitals.cls.value);
    });
    
    // First Contentful Paint (FCP)
    this.observePerformanceEntry('paint', (entries) => {
      const fcpEntry = entries.find(entry => entry.name === 'first-contentful-paint');
      if (fcpEntry) {
        this.vitals.fcp = {
          value: fcpEntry.startTime,
          timestamp: Date.now()
        };
        this.reportVital('FCP', this.vitals.fcp.value);
      }
    });
    
    // Time to First Byte (TTFB)
    window.addEventListener('load', () => {
      const navigationEntry = performance.getEntriesByType('navigation')[0];
      if (navigationEntry) {
        this.vitals.ttfb = {
          value: navigationEntry.responseStart - navigationEntry.requestStart,
          timestamp: Date.now()
        };
        this.reportVital('TTFB', this.vitals.ttfb.value);
      }
    });
  }
  
  observePerformanceEntry(type, callback) {
    try {
      const observer = new PerformanceObserver((list) => {
        callback(list.getEntries());
      });
      
      observer.observe({ type, buffered: true });
      this.observers.set(type, observer);
    } catch (error) {
      console.warn(`Performance observer for ${type} not supported:`, error);
    }
  }
  
  initUserInteractionTracking() {
    const trackInteraction = (type, target, duration = 0) => {
      const interaction = {
        type,
        element: target.tagName || 'unknown',
        className: target.className || '',
        id: target.id || '',
        timestamp: Date.now(),
        duration,
        url: window.location.href
      };
      
      this.currentSession.interactions.push(interaction);
      this.reportInteraction(interaction);
    };
    
    // Click tracking
    document.addEventListener('click', (event) => {
      trackInteraction('click', event.target);
    });
    
    // Scroll tracking (throttled)
    let scrollTimeout;
    document.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        trackInteraction('scroll', document.documentElement);
      }, 100);
    });
    
    // Form submission tracking
    document.addEventListener('submit', (event) => {
      trackInteraction('form-submit', event.target);
    });
    
    // Input focus tracking
    document.addEventListener('focusin', (event) => {
      if (event.target.matches('input, textarea, select')) {
        trackInteraction('input-focus', event.target);
      }
    });
    
    // Long task tracking
    this.observePerformanceEntry('longtask', (entries) => {
      entries.forEach(entry => {
        trackInteraction('long-task', document.body, entry.duration);
      });
    });
  }
  
  initResourceMonitoring() {
    // Monitor resource loading performance
    this.observePerformanceEntry('resource', (entries) => {
      entries.forEach(entry => {
        const resourceMetric = {
          name: entry.name,
          type: entry.initiatorType,
          duration: entry.duration,
          size: entry.transferSize || entry.encodedBodySize || 0,
          startTime: entry.startTime,
          responseEnd: entry.responseEnd,
          timestamp: Date.now()
        };
        
        this.metrics.set(`resource_${entry.name}`, resourceMetric);
        this.reportResourceMetric(resourceMetric);
      });
    });
    
    // Monitor failed resources
    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        this.reportResourceError({
          url: event.target.src || event.target.href,
          type: event.target.tagName,
          message: 'Resource failed to load',
          timestamp: Date.now()
        });
      }
    });
  }
  
  initNavigationTiming() {
    window.addEventListener('load', () => {
      const navigation = performance.getEntriesByType('navigation')[0];
      
      if (navigation) {
        const timing = {
          dns: navigation.domainLookupEnd - navigation.domainLookupStart,
          connect: navigation.connectEnd - navigation.connectStart,
          ttfb: navigation.responseStart - navigation.requestStart,
          download: navigation.responseEnd - navigation.responseStart,
          domReady: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
          load: navigation.loadEventEnd - navigation.loadEventStart,
          total: navigation.loadEventEnd - navigation.navigationStart
        };
        
        this.currentSession.performance.navigation = timing;
        this.reportNavigationTiming(timing);
      }
    });
  }
  
  initMemoryMonitoring() {
    // Monitor memory usage (Chrome only)
    if ('memory' in performance) {
      setInterval(() => {
        const memoryInfo = {
          used: performance.memory.usedJSHeapSize,
          total: performance.memory.totalJSHeapSize,
          limit: performance.memory.jsHeapSizeLimit,
          timestamp: Date.now()
        };
        
        this.metrics.set('memory', memoryInfo);
        this.reportMemoryUsage(memoryInfo);
      }, 30000); // Every 30 seconds
    }
  }
  
  initErrorTracking() {
    // JavaScript errors
    window.addEventListener('error', (event) => {
      this.reportError({
        type: 'javascript',
        message: event.message,
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: event.error?.stack,
        timestamp: Date.now()
      });
    });
    
    // Promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.reportError({
        type: 'promise',
        message: event.reason?.message || 'Unhandled Promise Rejection',
        stack: event.reason?.stack,
        timestamp: Date.now()
      });
    });
    
    // React error boundary integration
    window.addEventListener('react-error', (event) => {
      this.reportError({
        type: 'react',
        message: event.detail.message,
        componentStack: event.detail.componentStack,
        stack: event.detail.stack,
        timestamp: Date.now()
      });
    });
  }
  
  initNetworkMonitoring() {
    // Monitor network status
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.reportNetworkStatus('online');
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
      this.reportNetworkStatus('offline');
    });
    
    // Monitor connection quality
    if ('connection' in navigator) {
      const connection = navigator.connection;
      
      connection.addEventListener('change', () => {
        this.reportConnectionChange({
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
          saveData: connection.saveData,
          timestamp: Date.now()
        });
      });
    }
  }
  
  setupPeriodicReporting() {
    // Send metrics every 60 seconds
    setInterval(() => {
      this.sendMetricsBatch();
    }, 60000);
    
    // Send session data on page unload
    window.addEventListener('beforeunload', () => {
      this.endSession();
    });
    
    // Send session data on visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.sendMetricsBatch();
      } else {
        this.resumeSession();
      }
    });
  }
  
  // Reporting methods
  reportVital(name, value) {
    const vital = {
      name,
      value,
      rating: this.getVitalRating(name, value),
      url: window.location.href,
      timestamp: Date.now(),
      sessionId: this.currentSession.id
    };
    
    this.queueAnalytics('core-web-vital', vital);
    console.log(`Core Web Vital - ${name}:`, value, vital.rating);
  }
  
  reportInteraction(interaction) {
    this.queueAnalytics('user-interaction', {
      ...interaction,
      sessionId: this.currentSession.id
    });
  }
  
  reportResourceMetric(resource) {
    if (resource.duration > 1000 || resource.size > 1000000) {
      // Report slow or large resources
      this.queueAnalytics('resource-performance', {
        ...resource,
        sessionId: this.currentSession.id
      });
    }
  }
  
  reportResourceError(error) {
    this.currentSession.errors.push(error);
    this.queueAnalytics('resource-error', {
      ...error,
      sessionId: this.currentSession.id
    });
  }
  
  reportNavigationTiming(timing) {
    this.queueAnalytics('navigation-timing', {
      ...timing,
      sessionId: this.currentSession.id,
      url: window.location.href
    });
  }
  
  reportMemoryUsage(memory) {
    const memoryMB = {
      used: Math.round(memory.used / 1048576),
      total: Math.round(memory.total / 1048576),
      limit: Math.round(memory.limit / 1048576)
    };
    
    this.queueAnalytics('memory-usage', {
      ...memoryMB,
      sessionId: this.currentSession.id
    });
  }
  
  reportError(error) {
    this.currentSession.errors.push(error);
    this.queueAnalytics('error', {
      ...error,
      sessionId: this.currentSession.id,
      url: window.location.href
    });
    
    console.error('Performance Monitor - Error tracked:', error);
  }
  
  reportNetworkStatus(status) {
    this.queueAnalytics('network-status', {
      status,
      timestamp: Date.now(),
      sessionId: this.currentSession.id
    });
  }
  
  reportConnectionChange(connection) {
    this.queueAnalytics('connection-change', {
      ...connection,
      sessionId: this.currentSession.id
    });
  }
  
  // Analytics queue management
  queueAnalytics() {}
  
  flushAnalyticsQueue() {}
  
  sendMetricsBatch() {
    // Add current metrics to queue
    this.queueAnalytics('metrics-batch', {
      vitals: this.vitals,
      metrics: Object.fromEntries(this.metrics),
      session: this.currentSession,
      timestamp: Date.now()
    });
  }
  
  // Utility methods
  getVitalRating(name, value) {
    const thresholds = {
      LCP: { good: 2500, poor: 4000 },
      FID: { good: 100, poor: 300 },
      CLS: { good: 0.1, poor: 0.25 },
      FCP: { good: 1800, poor: 3000 },
      TTFB: { good: 600, poor: 1500 }
    };
    
    const threshold = thresholds[name];
    if (!threshold) return 'unknown';
    
    if (value <= threshold.good) return 'good';
    if (value <= threshold.poor) return 'needs-improvement';
    return 'poor';
  }
  
  getConnectionInfo() {
    if ('connection' in navigator) {
      const conn = navigator.connection;
      return {
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt,
        saveData: conn.saveData
      };
    }
    return null;
  }
  
  endSession() {
    this.currentSession.endTime = performance.now();
    this.currentSession.duration = this.currentSession.endTime - this.currentSession.startTime;
    
    this.queueAnalytics('session-end', this.currentSession);
    this.userSessions.push(this.currentSession);
  }
  
  resumeSession() {
    // Create new session for resumed activity
    this.currentSession = this.createSession();
    this.queueAnalytics('session-start', this.currentSession);
  }
  
  // Public API methods
  trackCustomEvent(name, data = {}) {
    this.queueAnalytics('custom-event', {
      name,
      data,
      sessionId: this.currentSession.id,
      url: window.location.href,
      timestamp: Date.now()
    });
  }
  
  trackPageView(url = window.location.href) {
    const pageView = {
      url,
      referrer: document.referrer,
      timestamp: Date.now(),
      sessionId: this.currentSession.id
    };
    
    this.currentSession.pageViews.push(pageView);
    this.queueAnalytics('page-view', pageView);
  }
  
  getPerformanceReport() {
    return {
      vitals: this.vitals,
      session: this.currentSession,
      metrics: Object.fromEntries(this.metrics),
      isOnline: this.isOnline
    };
  }
  
  // Cleanup
  destroy() {
    this.observers.forEach(observer => observer.disconnect());
    this.endSession();
  }
}

// Initialize global performance monitor
window.performanceMonitor = new PerformanceMonitor();

export default PerformanceMonitor;
